import React, { useCallback, useMemo } from 'react'
import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_HOTEL,
} from 'constants/offer'
import config from 'constants/config'
import { OverlayView, OverlayViewF } from '@react-google-maps/api'
import PropertyMarker from 'components/Common/PropertyMap/PropertyMarkerV2'
import { useIsMobileScreen } from 'hooks/useScreenSize'
import Clickable from 'components/Common/Clickable'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { getOfferId, getPositionAndOffsetOfTile } from 'components/Search/SearchResultsMap/utils'
import BusinessTravellerPropertyPriceMarker from './BusinessTravellerPropertyPriceMarker'
import { Position } from 'components/Search/type'
import PositionedMapTile from './PositionedMapTile'

const MAX_PIN_LIMIT = config.search.mapView.pinLimit
interface Props {
  onMarkerClick: (
    marker: App.SearchMapMarker,
    pinType: string,
    e?: Event | React.MouseEvent<Element | MouseEvent>
  ) => void;
  isActive: boolean;
  isHovered: boolean;
  isVisited: boolean;
  filters: App.OfferListFilters;
  onMarkerLoad?: (
    markerInstance: google.maps.Marker,
    markerData: App.SearchMapMarker
  ) => void;
  onMarkerUnmount?: (markerInstance: google.maps.Marker) => void;
  onDeselectOffers?: () => void;
  marker: App.SearchMapMarker;
  index: number;
  handleProductClick?: (
    offer:
      | App.Offer
      | App.OfferSummary
      | App.BedbankOffer
      | App.BedbankOfferSummary,
    position: number
  ) => void;
  mapContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  onHover?: (offerId: string, type: string) => void
  soldOut: boolean
  bestPrice?: number
  bestPriceDuration?: number
  bestPriceIsLoading?: boolean
  isSaved?: boolean
}

function HotelMapMarker(props: Props) {
  const {
    marker,
    index,
    onMarkerClick,
    isActive,
    isVisited,
    filters,
    onDeselectOffers,
    handleProductClick,
    mapContainerRef,
    isHovered,
    onHover,
    soldOut,
    bestPrice,
    bestPriceDuration,
    bestPriceIsLoading,
    isSaved,
  } = props

  const [tilePosition, setTilePosition] = React.useState<Position | undefined>(
    undefined,
  )
  const [tileOffset, setTileOffset] = React.useState<
    { xOffset: number; yOffset: number } | undefined
  >(undefined)

  const calculateZIndex = useCallback(
    (marker: App.SearchMapMarker, index: number) => {
      if (isActive) {
        return MAX_PIN_LIMIT + 1
      }
      if (isHovered) {
        return MAX_PIN_LIMIT
      }
      if (index >= MAX_PIN_LIMIT) {
        return 0
      }
      const offerType = marker.offers[0].type
      if (
        offerType === OFFER_TYPE_HOTEL ||
        offerType === OFFER_TYPE_ALWAYS_ON ||
        offerType === OFFER_TYPE_BED_BANK
      ) {
        return MAX_PIN_LIMIT - index
      }
      return 0
    },
    [isActive, isHovered],
  )

  const isMobile = useIsMobileScreen()

  const handleClick = useCallback(
    (e: Event | React.MouseEvent<Element | MouseEvent>) => {
      onMarkerClick(marker, index < MAX_PIN_LIMIT ? 'pricePin' : 'pill', e)
      if (!isMobile) {
        const { position, offset } = getPositionAndOffsetOfTile(
          e,
          mapContainerRef,
          marker.offers[0].type === OFFER_TYPE_BED_BANK,
        )
        setTilePosition(position)
        setTileOffset(offset)
      }
      e.stopPropagation()
    },
    [index, isMobile, mapContainerRef, marker, onMarkerClick],
  )

  const handleMouseEnter = useCallback(() => {
    if (onHover) {
      onHover(marker.id, marker.offers[0].type)
    }
  }, [marker.id, marker.offers, onHover])

  const handleMouseLeave = useCallback(() => {
    if (onHover) {
      onHover('', '')
    }
  }, [onHover])

  const offerIds = useMemo(() => marker.offers.map(getOfferId), [marker.offers])

  const zIndex = useMemo(() => calculateZIndex(marker, index), [calculateZIndex, index, marker])

  return (
    <OverlayViewF
      key={marker.id}
      position={{
        lat: marker.latitude ?? 0,
        lng: marker.longitude ?? 0,
      }}
      zIndex={zIndex}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <Clickable onClick={handleClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {(config.businessTraveller.currentAccountMode !== 'business' || !config.BUSINESS_TRAVELLER_SPLIT_MAP_VIEW_UPDATES_ENABLED) && <PropertyMarker
          key={marker.id}
          marker={marker}
          active={isActive}
          isHovered={isHovered}
          visited={isVisited}
          soldOut={soldOut}
          isSaved={isSaved}
        />}
        {config.businessTraveller.currentAccountMode === 'business' && config.BUSINESS_TRAVELLER_SPLIT_MAP_VIEW_UPDATES_ENABLED && !bestPriceIsLoading && <BusinessTravellerPropertyPriceMarker
          key={marker.id}
          isFlash={marker.offers[0].type === OFFER_TYPE_HOTEL}
          isActive={isActive || isHovered}
          isVisited={isVisited}
          bestPrice={bestPrice}
          bestPriceDuration={bestPriceDuration}
        />}
      </Clickable>
      <CSSBreakpoint min="tablet">
        {isActive && (
          <PositionedMapTile
            offerIds={offerIds}
            filters={filters}
            id={marker.id}
            tilePosition={tilePosition}
            tileOffset={tileOffset}
            onProductClick={handleProductClick}
            onDeselectOffers={onDeselectOffers}
          />
        )}
      </CSSBreakpoint>
    </OverlayViewF>
  )
}

export default React.memo(HotelMapMarker)
